import { Card, CardContent } from "@/components/ui/card";

interface TablePageSkeletonProps {
  hasHeader?: boolean;
  hasMetrics?: boolean;
  metricsCount?: number;
  rowCount?: number;
  columnCount?: number;
}

export function TablePageSkeleton({
  hasHeader = true,
  hasMetrics = true,
  metricsCount = 4,
  rowCount = 5,
  columnCount = 6,
}: TablePageSkeletonProps) {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      {hasHeader && (
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-slate-200 rounded-lg" />
            <div className="h-4 w-72 bg-slate-100 rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-32 bg-slate-100 rounded-lg" />
            <div className="h-10 w-40 bg-slate-200 rounded-lg" />
          </div>
        </div>
      )}

      {/* Metrics Cards skeleton */}
      {hasMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: metricsCount }).map((_, i) => (
            <Card key={i} className="border-slate-200/70 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-3 w-20 bg-slate-100 rounded" />
                    <div className="h-7 w-12 bg-slate-200 rounded" />
                  </div>
                  <div className="h-8 w-8 bg-slate-50 rounded-lg" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search Bar / Filters skeleton */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="h-10 flex-1 max-w-md bg-slate-100 rounded-lg border border-slate-200/60" />
        <div className="flex gap-2">
          <div className="h-10 w-24 bg-slate-50 rounded-lg border border-slate-200/60" />
          <div className="h-10 w-24 bg-slate-50 rounded-lg border border-slate-200/60" />
        </div>
      </div>

      {/* Table skeleton */}
      <Card className="border-slate-200/70 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="h-5 w-40 bg-slate-200 rounded" />
        </div>
        <div className="p-0">
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-slate-50/80 h-10 border-b border-slate-100 flex items-center px-4 gap-4">
              {Array.from({ length: columnCount }).map((_, i) => (
                <div key={i} className="h-3 bg-slate-200 rounded flex-1" />
              ))}
            </div>
            {/* Table Rows */}
            {Array.from({ length: rowCount }).map((_, i) => (
              <div key={i} className="h-16 border-b border-slate-50 flex items-center px-4 gap-4">
                {Array.from({ length: columnCount }).map((_, j) => (
                  <div key={j} className="h-3 bg-slate-100 rounded flex-1" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
